import { useNavigate } from 'react-router-dom'
import { Form, Field, Formik, FormikProps, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import apis from '../../libs/services'
import useCheckingEmail from '../../hooks/useCheckingEmail'
import { Amplify } from 'aws-amplify'
import { resetPassword } from 'aws-amplify/auth'
import DiamondWrapper from '../../components/global/diamongWrapper'
import { Button, Input, PageTitle, SkipLink } from '../../components/atoms'
import logo from '../../assets/images/logo.svg'
import { useAuth } from '../../hooks/useAuth'
import { useAuthContext } from '../../contexts/AuthContext'
import { ReactElement, useEffect } from 'react'
import useSetFocusById from '../../hooks/useSetFocus'
import ControlHighlight from '../../components/molecules/KeyboardControls/ControlHighlight'
import KeyboardControl from '../../components/molecules/KeyboardControls/KeyboardControl'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import { HighlightEffect } from '../../enums/KeyboardControls'
import ContactLinks from '../../components/molecules/ContactLinks/ContactLinks'
import ResetPasswordLink from '../../components/molecules/ResetPasswordLink'
import * as Sentry from '@sentry/react'
import { logger } from '../../utils'

export const pageTitle = 'Log in Page'
export const idLoginPageMainContent = 'login-page-main-content'

interface FormValues {
  password: string
};

const initialValues = {
  password: ''
}

const LoginFormSchema = Yup.object().shape({
  password: Yup.string().required('This field is required!')
})

export enum FocusableElement {
  InputPassword = 'input-password',
  ButtonContinue = 'button-continue',
  ResetPassword = 'link-reset-password',
  LinkContact = 'link-contact',
}

function Login (): ReactElement {
  const navigate = useNavigate()
  const { email } = useCheckingEmail()
  const { setUserProfile } = useAuth()
  const { signin } = useAuthContext()
  const keyboardControls = useKeyboardControls()
  const [setFocusById] = useSetFocusById()

  useEffect(() => {
    setFocusById(FocusableElement.InputPassword)
  }, [setFocusById])

  const handleResetPassword = async (): Promise<void> => {
    try {
      if (!email) {
        logger.error('Email is required to reset password')
        return
      }
      const currentConfig = Amplify.getConfig()
      logger.log('Full Amplify configuration:')
      logger.log(JSON.stringify(currentConfig))
      const result = await resetPassword({ username: email })
      logger.log('Password reset request result:')
      logger.log(JSON.stringify(result))
      navigate('/password-reset', { state: { email } })
    } catch (error) {
      logger.error('Error requesting password reset:')
      logger.error(error as any)
      // You could add a more sophisticated error handling here
      alert('Failed to request password reset. Please try again.')
    }
  }

  const handleFormSubmit = async (values: FormValues, actions: FormikHelpers<FormValues>): Promise<any> => {
    try {
      const res = await signin(String(email), values.password)
      if (res.isSignedIn === true) {
        const profileRes = await apis.getUserProfile()
        const profileData = profileRes?.data?.data
        const sentryId = profileData?.id ?? 'unknown' // Using nullish coalescing
        Sentry.setUser({
          id: sentryId
        })
        setUserProfile(profileData)
        if (profileData.user_profile.accepted_terms === true) {
          if (profileData.role.slug === 'admin') {
            navigate('/dashboard/all-assigned-students')
          } else {
            // ProtectedRoute's survey guard will redirect to survey if needed
            navigate('/user-dashboard')
          }
        } else {
          navigate('/terms-of-use')
        }
      }
    } catch (err: any) {
      actions.setSubmitting(false)
      const errorMessage = typeof err?.response?.data?.error_description === 'string'
        ? err.response.data.error_description
        : 'Password is not correct!'
      actions.setFieldError('password', errorMessage)
    } finally {
      actions.setSubmitting(false)
    }
  }

  return (
    <div className='bg-black min-h-[100dvh] font-primary items-center flex justify-center'>
      <SkipLink targetId={idLoginPageMainContent} />
      <header className='absolute left-[40px] top-[40px]'>
        <img src={logo} className='w-[138px]' alt='logo' />
      </header>
      <section className='flex justify-center'>
        <Formik
          initialValues={initialValues}
          validationSchema={LoginFormSchema}
          onSubmit={handleFormSubmit}
        >
          {({ errors, isSubmitting, touched }: FormikProps<FormValues>) => (
            <DiamondWrapper className='pb-[77px]'>
              <div className='p-[22px] flex flex-col items-center'>
                <main
                  id={idLoginPageMainContent}
                  tabIndex={-1}
                >
                  <PageTitle title={pageTitle} addScreenReaderH1={false} />
                  <h1 className='font-bold capitalize text-[26px] text-dopage-lightGreen text-center'>
                    log in
                  </h1>

                  <div className='min-w-[440px] w-full mt-[80px]'>
                    <Form>
                      <Input className='w-full py-[30px] px-[27px] my-[10px] text-[26px]' aria-label='email' type='email' placeholder='Email' value={email} readOnly />
                      <ControlHighlight
                        active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.InputPassword)}
                        highlightEffect={HighlightEffect.InputOutline}
                        data-testid={`highlight-${FocusableElement.InputPassword}`}
                        activeClassName='mt-[26px] mb-[8px]'
                      >
                        <label aria-label='password'>
                          <Field name='password' type='password'>
                            {({
                              field
                            }: any) => (
                              <KeyboardControl>
                                <Input
                                  id={FocusableElement.InputPassword}
                                  data-testid={FocusableElement.InputPassword}
                                  className='w-full py-[30px] px-[27px] text-[26px] border-dopage-primary'
                                  type='password'
                                  placeholder='Password'
                                  {...field}
                                />
                              </KeyboardControl>
                            )}
                          </Field>
                        </label>
                      </ControlHighlight>
                      {(errors.password !== undefined &&
                        errors.password !== '' &&
                        touched.password === true)
                        ? (
                          <div
                            className='text-[#ff0000] pt-1'
                            aria-label={`Error message: ${errors.password}`}
                          >
                            {errors.password}
                          </div>
                          )
                        : null}
                      <ControlHighlight
                        active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonContinue)}
                        highlightEffect={HighlightEffect.ButtonHexOutline}
                        data-testid={`highlight-${FocusableElement.ButtonContinue}`}
                      >
                        <KeyboardControl>
                          <Button
                            className='diamond-btn h-[86px] w-full font-bold mt-[32px] flex justify-center items-center'
                            data-testid={FocusableElement.ButtonContinue}
                            disabled={isSubmitting}
                            id={FocusableElement.ButtonContinue}
                            loading={isSubmitting}
                            type='submit'
                          >
                            <span className='text-black text-[26px] text-neutral-090'>Continue</span>
                          </Button>
                        </KeyboardControl>
                      </ControlHighlight>
                      <ResetPasswordLink
                        id={FocusableElement.ResetPassword}
                        onClick={handleResetPassword}
                      />
                      <ContactLinks
                        id={FocusableElement.LinkContact}
                      />
                    </Form>
                  </div>
                </main>
              </div>
            </DiamondWrapper>
          )}
        </Formik>
      </section>
      <footer className='sr-only' />
    </div>
  )
}

export default Login
